import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { queryClient } from '../lib/queryClient'
import { findStaffSessionStr } from '../services/auth.service'
import type { User } from '@supabase/supabase-js'

export const AUTH_USER_KEY = ['auth', 'user'] as const

const authQueryOptions = {
  queryKey: [...AUTH_USER_KEY],
  queryFn: async (): Promise<User | null> => {
    // First try getUser() which validates with the server
    const { data: { user }, error } = await supabase.auth.getUser()
    if (user) return user

    // Fallback: check getSession() for cross-tab scenarios where the Supabase
    // client may not have finished loading the session from localStorage yet.
    if (error || !user) {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        if (import.meta.env.DEV) console.info('[Auth] getUser failed but getSession found user — cross-tab session recovered')
        return session.user
      }
    }

    // Only warn when it's unexpected — staff-session logins have no Supabase JWT by design.
    if (error && import.meta.env.DEV && !findStaffSessionStr()) {
        console.warn('[Auth] getUser error:', error.message)
    }
    return null
  },
  staleTime: 5 * 60 * 1000,
}

/** React hook — use inside components */
export function useAuthUser() {
  return useQuery(authQueryOptions)
}

/** Imperative — use in async functions / callbacks */
export async function getAuthUser(): Promise<User | null> {
  return queryClient.fetchQuery(authQueryOptions)
}

/** Mark cached user stale so next access refetches (use on auth state change) */
export function invalidateAuthUser() {
  queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY })
}

/** Set cached user to null immediately (use on sign-out) */
export function clearAuthUser() {
  queryClient.setQueryData(AUTH_USER_KEY, null)
}
